import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import AttendanceGate from '../components/AttendanceGate';
import { useAttendanceGate } from '../hooks/useAttendanceGate';
import { useWindowedList } from '../hooks/useWindowedList';
import SearchableSelect from '../components/SearchableSelect';
import ContactsField, { emptyContact } from '../components/ContactsField';
import { SEGMENT_UOM, SEGMENTS, ACTIVATION_TYPES, toSegmentVolumes } from '../lib/outletSegments';
import GeoLocationCapture from '../components/GeoLocationCapture';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  MapPin, 
  Plus, 
  Search, 
  Loader2, 
  Clock, 
  User,
  Phone,
  MessageSquare,
  ChevronRight,
  Building,
  Store,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const Visits = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchVisits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVisits = async () => {
    try {
      const response = await api.get('/visits');
      setVisits(response.data);
    } catch (error) {
      console.error('Failed to fetch visits:', error);
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  };

  const term = searchQuery.trim().toLowerCase();
  const filteredVisits = !term
    ? visits
    : visits.filter((visit) =>
        [visit.entity_name, visit.entity_code, visit.contact_person, visit.purpose, visit.visit_by_name]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(term))
      );

  // Visits accumulate quickly, so only a screenful is kept in the DOM at a time.
  const windowed = useWindowedList(filteredVisits);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4 fade-in" data-testid="visits-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Visits</h1>
            <p className="text-sm text-slate-500">Track your field visits</p>
          </div>
          <Button
            onClick={() => navigate('/visits/new')}
            data-testid="new-visit-btn"
            className="h-10 px-4 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Visit
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search visits..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-12 pl-10 rounded-xl border-slate-200 bg-white"
            data-testid="search-visits"
          />
        </div>

        {/* Visits List */}
        <div className="space-y-3">
          {filteredVisits.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500 mb-4">No visits found</p>
              <Button
                onClick={() => navigate('/visits/new')}
                className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl"
              >
                Create First Visit
              </Button>
            </div>
          ) : (
            windowed.shown.map((visit) => (
              <div
                key={visit.id}
                className="bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-all card-hover"
                data-testid={`visit-card-${visit.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                      visit.outlet_id ? "bg-pink-100" : visit.distributor_id ? "bg-indigo-100" : "bg-slate-100"
                    )}>
                      {visit.outlet_id ? (
                        <Store className="w-5 h-5 text-pink-600" />
                      ) : visit.distributor_id ? (
                        <Building className="w-5 h-5 text-indigo-600" />
                      ) : (
                        <MapPin className="w-5 h-5 text-slate-500" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                        {visit.entity_name || 'Visit'}
                        {visit.entity_code && (
                          <span className="text-xs font-normal text-slate-400">{visit.entity_code}</span>
                        )}
                        {visit.is_first_visit && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
                            FIRST VISIT
                          </span>
                        )}
                      </h3>
                      {visit.contact_person && (
                        <p className="text-sm text-slate-600">
                          {visit.contact_person}
                          {visit.contact_no && (
                            <span className="text-slate-400"> · {visit.contact_no}</span>
                          )}
                        </p>
                      )}
                      <p className="text-sm text-slate-500">{visit.purpose || 'No purpose specified'}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(visit.created_at)} at {formatTime(visit.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={cn(
                    "px-2.5 py-1 rounded-full text-xs font-medium",
                    visit.status === 'approved' && "bg-emerald-100 text-emerald-700",
                    visit.status === 'pending' && "bg-amber-100 text-amber-700",
                    visit.status === 'rejected' && "bg-red-100 text-red-700"
                  )}>
                    {visit.status}
                  </span>
                </div>
              </div>
            ))
          )}

        {windowed.hasMore && (
          <button
            type="button"
            onClick={windowed.showMore}
            className="w-full bg-white rounded-2xl border border-slate-200 p-3 text-sm font-medium text-sky-700 hover:bg-slate-50"
            data-testid="show-more"
          >
            Show more visits · {windowed.shown.length} of {windowed.total}
          </button>
        )}
        </div>
      </div>
    </Layout>
  );
};

// New Visit Page Component
export const NewVisit = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  // KEYNOTE 15/16: a visit can only be recorded while the day is open.
  const gate = useAttendanceGate();
  const [step, setStep] = useState(1);
  const [location, setLocation] = useState(null);
  const [nearbyData, setNearbyData] = useState({ distributors: [], outlets: [] });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [entityType, setEntityType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [purposes, setPurposes] = useState([]);
  const [distributorTypes, setDistributorTypes] = useState([]);
  // Other visits are off unless the super admin has switched them on.
  const [otherVisitEnabled, setOtherVisitEnabled] = useState(false);
  // Everything the two on-the-fly forms need, so they offer exactly the same
  // admin-managed dropdowns as the full Distributor and Outlet screens.
  const [masters, setMasters] = useState({
    routes: [], zones: [], retailerTypes: [], assets: [], cities: [], distributors: []
  });
  const [formData, setFormData] = useState({
    purpose: '',
    contact_person: '',
    contact_no: ''
  });
  // KEYNOTE 5/9: on a first visit the user creates the customer/distributor here.
  // Mirrors the Add Distributor form: the code is generated server-side, so it is
  // not asked for, and route/zone are master ids rather than typed text.
  const [newDistributor, setNewDistributor] = useState({
    name: '',
    route_id: '',
    zone_id: '',
    distributor_type: '',
    city: '',
    address: '',
    pincode: ''
  });

  // KEYNOTE 7/10: an outlet can equally be registered during the visit. Mirrors
  // the Add Outlet form field for field.
  const [newOutlet, setNewOutlet] = useState({
    outlet_name: '',
    distributor_id: '',
    route: '',
    zone: '',
    retailer_type_id: '',
    city: '',
    asset_id: '',
    activation_type: '',
    pincode: '',
    address: ''
  });
  const [outletVolumes, setOutletVolumes] = useState(() =>
    Object.fromEntries(SEGMENTS.map((seg) => [seg, '']))
  );
  // Same multi-contact list the Distributor and Outlet screens use.
  const [newDistributorContacts, setNewDistributorContacts] = useState([emptyContact(true)]);
  const [newOutletContacts, setNewOutletContacts] = useState([emptyContact(true)]);

  useEffect(() => {
    // Purpose options are admin-controlled (KEYNOTE 14).
    // Each kind of visit has its own admin-managed purpose list (KEYNOTE 14).
    const source = entityType === 'other'
      ? '/masters/other-visit-purposes'
      : (entityType === 'outlet' || entityType === 'new-outlet')
        ? '/masters/outlet-purposes'
        : '/masters/visit-purposes';
    api.get(source)
      .then((res) => setPurposes(res.data))
      .catch(() => setPurposes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType]);

  useEffect(() => {
    // Distributor type is master driven, same as the purpose list.
    api.get('/masters/distributor-types')
      .then((res) => setDistributorTypes(res.data))
      .catch(() => setDistributorTypes([]));

    api.get('/settings')
      .then((res) => setOtherVisitEnabled(Boolean(res.data?.other_visit_enabled)))
      .catch(() => setOtherVisitEnabled(false));

    // The rest of the admin-managed lists the two capture forms rely on.
    Promise.all([
      api.get('/masters/routes'),
      api.get('/masters/retailer-types'),
      api.get('/masters/assets'),
      api.get('/masters/cities'),
      api.get('/distributors')
    ])
      .then(([routes, retailerTypes, assets, cities, distributors]) =>
        setMasters((prev) => ({
          ...prev,
          routes: routes.data,
          retailerTypes: retailerTypes.data,
          assets: assets.data,
          cities: cities.data,
          distributors: distributors.data
        }))
      )
      .catch(() => toast.error('Could not load the master lists'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zones belong to a route, exactly as on the Distributor form.
  useEffect(() => {
    if (!newDistributor.route_id) {
      setMasters((prev) => ({ ...prev, zones: [] }));
      return;
    }
    api.get(`/masters/zones?route_id=${newDistributor.route_id}`)
      .then((res) => setMasters((prev) => ({ ...prev, zones: res.data })))
      .catch(() => setMasters((prev) => ({ ...prev, zones: [] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDistributor.route_id]);

  const handleLocationCapture = async (loc) => {
    setLocation(loc);
    setDetecting(true);

    try {
      const response = await api.post('/visits/detect-nearby', { lat: loc.lat, lng: loc.lng });
      setNearbyData(response.data);
      setStep(2);
    } catch (error) {
      console.error('Failed to detect nearby:', error);
      toast.error('Failed to detect nearby locations');
    } finally {
      setDetecting(false);
    }
  };

  const handleSelectEntity = (entity, type) => {
    setSelectedEntity(entity);
    setEntityType(type);
    setFormData((prev) => ({
      ...prev,
      contact_person: prev.contact_person || entity?.contacts?.[0]?.name || '',
      contact_no: prev.contact_no || entity?.contacts?.[0]?.phone || ''
    }));
    setStep(3);
  };

  const handleNewCustomer = () => {
    setSelectedEntity(null);
    setEntityType('new');
    setStep(3);
  };

  const handleNewOutlet = () => {
    setSelectedEntity(null);
    setEntityType('new-outlet');
    setStep(3);
  };

  const handleOtherVisit = () => {
    setSelectedEntity(null);
    setEntityType('other');
    setStep(3);
  };

  // Picking the parent fills in route and zone, the same courtesy the Add Outlet
  // screen does - the user can still correct them.
  const handleOutletParent = (distributorId) => {
    const parent = masters.distributors.find((d) => d.id === distributorId);
    setNewOutlet((prev) => ({
      ...prev,
      distributor_id: distributorId,
      route: parent?.route || prev.route,
      zone: parent?.zone || prev.zone
    }));
  };

  const isNew = entityType === 'new';
  const isNewOutlet = entityType === 'new-outlet';
  const isOtherVisit = entityType === 'other';

  // A blank row is just an unused slot, not a contact.
  const filledContacts = (rows) =>
    rows.filter((c) => (c.name || '').trim() || (c.phone || '').trim());

  const cityOptions = useMemo(
    () => (masters.cities || []).map((c) => ({ value: c.name, label: c.name })),
    [masters.cities]
  );
  const distributorOptions = useMemo(
    () => (masters.distributors || []).map((d) => ({
      value: d.id,
      label: d.code ? `${d.name} (${d.code})` : d.name
    })),
    [masters.distributors]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!location) {
      toast.error('Please capture your location first');
      return;
    }
    if (!formData.purpose) {
      toast.error('Please select the purpose of this visit');
      return;
    }
    if (isNew) {
      if (!String(newDistributor.name || '').trim()) {
        toast.error('Enter the customer/distributor name');
        return;
      }
      if (!newDistributor.route_id) {
        toast.error('Select a route for the new customer/distributor');
        return;
      }
      // Required, same as on the admin form - the API rejects a blank type.
      if (!newDistributor.distributor_type) {
        toast.error('Select the customer/distributor type');
        return;
      }
      if (!String(newDistributor.city || '').trim()) {
        toast.error('Select the city for the new customer/distributor');
        return;
      }
    }
    if (isOtherVisit) {
      // Nothing is on file for an other visit, so nothing can be left out.
      if (!String(formData.contact_person || '').trim()) {
        toast.error('Enter the contact name for this visit');
        return;
      }
      if (!String(formData.contact_no || '').trim()) {
        toast.error('Enter the contact number for this visit');
        return;
      }
    }
    if (isNewOutlet) {
      const required = {
        outlet_name: 'outlet name',
        distributor_id: 'parent customer/distributor',
        route: 'route',
        zone: 'zone',
        retailer_type_id: 'retailer type',
        city: 'city',
        asset_id: 'asset',
        activation_type: 'activation type'
      };
      const missing = Object.keys(required).find((f) => !String(newOutlet[f] || '').trim());
      if (missing) {
        toast.error(`Enter the ${required[missing]} for the new outlet`);
        return;
      }
      // Every segment answered for; zero counts, blank does not.
      const blankSegments = SEGMENTS.filter((seg) => String(outletVolumes[seg] ?? '').trim() === '');
      if (blankSegments.length) {
        toast.error(`Enter a volume for: ${blankSegments.join(', ')} (use 0 if not stocked)`);
        return;
      }
    }

    setLoading(true);
    try {
      const visitData = {
        geo_location: { lat: location.lat, lng: location.lng },
        distributor_id: entityType === 'distributor' ? selectedEntity?.id : null,
        outlet_id: entityType === 'outlet' ? selectedEntity?.id : null,
        purpose: formData.purpose,
        contact_person: formData.contact_person,
        contact_no: formData.contact_no,
        new_distributor: isNew
          ? {
              ...newDistributor,
              contacts: filledContacts(newDistributorContacts),
              contact_person: formData.contact_person,
              contact_no: formData.contact_no
            }
          : null,
        new_outlet: isNewOutlet
          ? {
              ...newOutlet,
              purpose: formData.purpose,
              segment_volumes: toSegmentVolumes(outletVolumes),
              contacts: filledContacts(newOutletContacts).length
                ? filledContacts(newOutletContacts)
                : (formData.contact_person || formData.contact_no)
                  ? [{
                      name: formData.contact_person || '',
                      phone: formData.contact_no || '',
                      is_primary: true
                    }]
                  : []
            }
          : null,
        is_other_visit: isOtherVisit,
        is_manual_entry: isNew || isNewOutlet || isOtherVisit
      };

      await api.post('/visits', visitData);
      toast.success(
        isNew
          ? 'Visit recorded. The new customer/distributor is pending admin approval.'
          : isNewOutlet
            ? 'Visit recorded. The new outlet is pending admin approval.'
            : isOtherVisit
              ? 'Other visit recorded. It counts once the admin approves it.'
              : 'Visit recorded successfully!'
      );
      navigate('/visits');
    } catch (error) {
      console.error('Failed to create visit:', error);
      toast.error(error.response?.data?.detail || 'Failed to record visit');
    } finally {
      setLoading(false);
    }
  };

  if (gate.loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      </Layout>
    );
  }

  if (!gate.allowed) {
    return (
      <Layout>
        <div className="py-10">
          <AttendanceGate reason={gate.reason} punchOutTime={gate.punchOutTime} action="a visit" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="new-visit-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">New Visit</h1>
          <p className="text-sm text-slate-500">Record a new field visit</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                step >= s ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-500"
              )}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={cn(
                  "flex-1 h-1 rounded-full transition-all",
                  step > s ? "bg-sky-600" : "bg-slate-200"
                )} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Location Capture */}
        {step === 1 && (
          <div className="space-y-4">
            <GeoLocationCapture onLocationCapture={handleLocationCapture} />
            {detecting && (
              <div className="flex items-center justify-center gap-2 p-4 bg-sky-50 rounded-xl">
                <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
                <span className="text-sm text-sky-700">Detecting nearby distributors & outlets...</span>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Entity */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span className="text-sm text-emerald-700">Location captured successfully</span>
            </div>

            {nearbyData.distributors.length > 0 && (
              <div>
                <h3 className="font-medium text-slate-900 mb-3">Nearby Distributors</h3>
                <div className="space-y-2">
                  {nearbyData.distributors.map((dist) => (
                    <button
                      key={dist.id}
                      onClick={() => handleSelectEntity(dist, 'distributor')}
                      className="w-full bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-sky-300 hover:shadow-md transition-all"
                      data-testid={`select-distributor-${dist.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                            <Building className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{dist.name}</p>
                            <p className="text-sm text-slate-500">Code: {dist.code}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {nearbyData.outlets.length > 0 && (
              <div>
                <h3 className="font-medium text-slate-900 mb-3">Nearby Outlets</h3>
                <div className="space-y-2">
                  {nearbyData.outlets.map((outlet) => (
                    <button
                      key={outlet.id}
                      onClick={() => handleSelectEntity(outlet, 'outlet')}
                      className="w-full bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-sky-300 hover:shadow-md transition-all"
                      data-testid={`select-outlet-${outlet.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                            <Store className="w-5 h-5 text-pink-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{outlet.outlet_name}</p>
                            <p className="text-sm text-slate-500">ID: {outlet.outlet_id}</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {nearbyData.distributors.length === 0 && nearbyData.outlets.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="text-sm text-amber-700">No nearby distributors or outlets found</span>
              </div>
            )}

            <Button
              onClick={handleNewCustomer}
              variant="outline"
              className="w-full h-12 rounded-xl border-slate-200"
              data-testid="new-customer-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Customer / Distributor
            </Button>
            <Button
              onClick={handleNewOutlet}
              variant="outline"
              className="w-full h-12 rounded-xl border-slate-200"
              data-testid="new-outlet-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Outlet
            </Button>
            {otherVisitEnabled && (
              <Button
                onClick={handleOtherVisit}
                variant="outline"
                className="w-full h-12 rounded-xl border-slate-200"
                data-testid="other-visit-btn"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Other Visit
              </Button>
            )}
            <p className="text-xs text-slate-500 text-center">
              First visit to this party? Add it here — the details are captured once and
              approved by the admin.
            </p>
          </div>
        )}

        {/* Step 3: Visit Details */}
        {step === 3 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {selectedEntity && (
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-4">
                <p className="text-xs text-sky-600 font-medium uppercase tracking-wider mb-1">
                  {entityType === 'distributor' ? 'Distributor' : 'Outlet'}
                </p>
                <p className="font-medium text-slate-900">
                  {entityType === 'distributor' ? selectedEntity.name : selectedEntity.outlet_name}
                </p>
              </div>
            )}

            {isOtherVisit && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <h3 className="font-medium text-slate-900">Other Visit</h3>
                </div>
                <p className="text-xs text-slate-500">
                  Not against a customer/distributor or outlet. Your location, the purpose and
                  the contact details below are all required, and the visit only counts once the
                  admin has approved it.
                </p>
              </div>
            )}

            {/* New customer/distributor - captured once, by the user, on the first visit */}
            {isNew && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4 text-sky-600" />
                  <h3 className="font-medium text-slate-900">New Customer / Distributor</h3>
                </div>
                <p className="text-xs text-slate-500 -mt-2">
                  Entered once only. The address is taken from your captured GPS location.
                </p>

                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={newDistributor.name}
                    onChange={(e) => setNewDistributor({ ...newDistributor, name: e.target.value })}
                    placeholder="Distributor name"
                    className="h-11 rounded-xl"
                    data-testid="new-dist-name"
                  />
                  <p className="text-xs text-slate-500">
                    The distributor code is generated automatically once this is saved.
                  </p>
                </div>

                {/* Route and Zone come from the masters, exactly as on the
                    Add Distributor screen. Zones belong to the chosen route. */}
                <div className="space-y-2">
                  <Label>Route *</Label>
                  <Select
                    value={newDistributor.route_id}
                    onValueChange={(value) =>
                      setNewDistributor({ ...newDistributor, route_id: value, zone_id: '' })
                    }
                  >
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-dist-route">
                      <SelectValue placeholder="Select route" />
                    </SelectTrigger>
                    <SelectContent>
                      {masters.routes.map((r) => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {masters.routes.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No routes configured — ask the admin to add them under Masters.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Zone</Label>
                  <Select
                    value={newDistributor.zone_id}
                    onValueChange={(value) => setNewDistributor({ ...newDistributor, zone_id: value })}
                    disabled={!newDistributor.route_id}
                  >
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-dist-zone">
                      <SelectValue
                        placeholder={newDistributor.route_id ? 'Select zone' : 'Pick a route first'}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {masters.zones.map((z) => (
                        <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {newDistributor.route_id && masters.zones.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No zones configured for this route.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Pincode</Label>
                    <Input
                      value={newDistributor.pincode}
                      onChange={(e) => setNewDistributor({ ...newDistributor, pincode: e.target.value })}
                      placeholder="Pincode"
                      className="h-11 rounded-xl"
                      data-testid="new-dist-pincode"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={newDistributor.address}
                      onChange={(e) => setNewDistributor({ ...newDistributor, address: e.target.value })}
                      placeholder="Optional"
                      className="h-11 rounded-xl"
                      data-testid="new-dist-address"
                    />
                  </div>
                </div>

                <ContactsField
                  contacts={newDistributorContacts}
                  onChange={setNewDistributorContacts}
                  testIdPrefix="new-dist-contact"
                />

                <div className="space-y-2">
                  <Label>Distributor Type *</Label>
                  <Select
                    value={newDistributor.distributor_type}
                    onValueChange={(value) =>
                      setNewDistributor({ ...newDistributor, distributor_type: value })
                    }
                  >
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-dist-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {distributorTypes.map((t) => (
                        <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {distributorTypes.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No distributor types configured — ask the admin to add them under Masters.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>City *</Label>
                  <SearchableSelect
                    testId="new-dist-city"
                    value={newDistributor.city}
                    onChange={(value) => setNewDistributor({ ...newDistributor, city: value })}
                    options={cityOptions}
                    placeholder="Select city"
                    searchPlaceholder="Type a city name..."
                    emptyMessage="No city matches that"
                  />
                </div>
              </div>
            )}

            {/* New outlet - the same field set as the Add Outlet screen (KEYNOTE 7/10) */}
            {isNewOutlet && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-pink-600" />
                  <h3 className="font-medium text-slate-900">New Outlet</h3>
                </div>
                <p className="text-xs text-slate-500 -mt-2">
                  Entered once only. The address is taken from your captured GPS location, and the
                  outlet ID is generated on save.
                </p>

                <div className="space-y-2">
                  <Label>Under Customer / Distributor *</Label>
                  <SearchableSelect
                    testId="new-outlet-distributor"
                    value={newOutlet.distributor_id}
                    onChange={handleOutletParent}
                    options={distributorOptions}
                    placeholder="Select the parent distributor"
                    searchPlaceholder="Type a distributor name..."
                    emptyMessage="No distributor matches that"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Outlet Name *</Label>
                  <Input
                    value={newOutlet.outlet_name}
                    onChange={(e) => setNewOutlet({ ...newOutlet, outlet_name: e.target.value })}
                    placeholder="Outlet name"
                    className="h-11 rounded-xl"
                    data-testid="new-outlet-name"
                  />
                </div>

                {/* Analytical route and zone, typed as on the Add Outlet screen,
                    pre-filled from the parent distributor. */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Route *</Label>
                    <Input
                      value={newOutlet.route}
                      onChange={(e) => setNewOutlet({ ...newOutlet, route: e.target.value })}
                      placeholder="Analytical route"
                      className="h-11 rounded-xl"
                      data-testid="new-outlet-route"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zone *</Label>
                    <Input
                      value={newOutlet.zone}
                      onChange={(e) => setNewOutlet({ ...newOutlet, zone: e.target.value })}
                      placeholder="Analytical zone"
                      className="h-11 rounded-xl"
                      data-testid="new-outlet-zone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Retailer Type *</Label>
                  <Select
                    value={newOutlet.retailer_type_id}
                    onValueChange={(value) => setNewOutlet({ ...newOutlet, retailer_type_id: value })}
                  >
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-outlet-retailer-type">
                      <SelectValue placeholder="Select retailer type" />
                    </SelectTrigger>
                    <SelectContent>
                      {masters.retailerTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {masters.retailerTypes.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No retailer types configured - ask the admin to add them under Masters.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>City *</Label>
                  <SearchableSelect
                    testId="new-outlet-city"
                    value={newOutlet.city}
                    onChange={(value) => setNewOutlet({ ...newOutlet, city: value })}
                    options={cityOptions}
                    placeholder="Select city"
                    searchPlaceholder="Type a city name..."
                    emptyMessage="No city matches that"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Asset in Outlet *</Label>
                  <Select
                    value={newOutlet.asset_id}
                    onValueChange={(value) => setNewOutlet({ ...newOutlet, asset_id: value })}
                  >
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-outlet-asset">
                      <SelectValue placeholder="Select asset" />
                    </SelectTrigger>
                    <SelectContent>
                      {masters.assets.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {masters.assets.length === 0 && (
                    <p className="text-xs text-amber-600">
                      No assets configured - ask the admin to add them under Masters.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Activation Type</Label>
                  <Select
                    value={newOutlet.activation_type}
                    onValueChange={(value) => setNewOutlet({ ...newOutlet, activation_type: value })}
                  >
                    <SelectTrigger className="h-11 rounded-xl" data-testid="new-outlet-activation">
                      <SelectValue placeholder="Optional" />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIVATION_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Pincode</Label>
                    <Input
                      value={newOutlet.pincode}
                      onChange={(e) => setNewOutlet({ ...newOutlet, pincode: e.target.value })}
                      placeholder="Pincode"
                      className="h-11 rounded-xl"
                      data-testid="new-outlet-pincode"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={newOutlet.address}
                      onChange={(e) => setNewOutlet({ ...newOutlet, address: e.target.value })}
                      placeholder="Optional"
                      className="h-11 rounded-xl"
                      data-testid="new-outlet-address"
                    />
                  </div>
                </div>

                <ContactsField
                  contacts={newOutletContacts}
                  onChange={setNewOutletContacts}
                  testIdPrefix="new-outlet-contact"
                />

                {/* Per-segment volumes - every segment listed, quantities optional */}
                <div className="space-y-2">
                  <Label>Volume by Segment</Label>
                  <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
                    {SEGMENTS.map((seg) => (
                      <div key={seg} className="flex items-center gap-3 px-3 py-2">
                        <span className="flex-1 text-sm text-slate-700">{seg}</span>
                        <Input
                          type="number"
                          min="0"
                          inputMode="decimal"
                          value={outletVolumes[seg]}
                          onChange={(e) =>
                            setOutletVolumes({ ...outletVolumes, [seg]: e.target.value })
                          }
                          placeholder="0"
                          className="h-9 w-24 rounded-lg text-right"
                          data-testid={`new-outlet-volume-${seg.replace(/\s+/g, "-").toLowerCase()}`}
                        />
                        <span className="w-10 text-xs text-slate-400">{SEGMENT_UOM[seg]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Purpose *</Label>
              <Select
                value={formData.purpose}
                onValueChange={(value) => setFormData({ ...formData, purpose: value })}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="purpose-select">
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent>
                  {purposes.map((p) => (
                    <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {purposes.length === 0 && (
                <p className="text-xs text-amber-600">
                  No purpose options configured — ask the admin to add them under Masters.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Contact Person {isOtherVisit ? '*' : ''}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Enter contact person name"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className="h-12 pl-10 rounded-xl"
                  data-testid="contact-person-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Contact No. {isOtherVisit ? '*' : ''}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Enter contact number"
                  value={formData.contact_no}
                  onChange={(e) => setFormData({ ...formData, contact_no: e.target.value })}
                  className="h-12 pl-10 rounded-xl"
                  data-testid="contact-no-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(2)}
                className="flex-1 h-12 rounded-xl"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 rounded-xl bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-200"
                data-testid="submit-visit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Visit'
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default Visits;
